import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { PageSpinner, EmptyState } from "../components/ui.js";
import { IconCheck, IconLeaf } from "../components/icons.js";

interface Item { id: string; name: string; department: string; quantity: string | null; checked: boolean }
interface List { id: string; name: string; items: Item[] }

export function GroceryPage() {
  const [list, setList] = useState<List | null>(null);
  const [departments, setDepartments] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [dept, setDept] = useState("Other");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ lists: List[]; departments: string[] }>("/api/grocery");
      setList(data.lists[0] ?? null);
      setDepartments(data.departments);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load grocery list.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function add() {
    if (!name.trim() || !list) return;
    await api.post("/api/grocery/items", { listId: list.id, name: name.trim(), department: dept }).catch(() => undefined);
    setName("");
    await load();
  }
  async function toggle(item: Item) {
    // Optimistic.
    setList((prev) => prev ? { ...prev, items: prev.items.map((i) => i.id === item.id ? { ...i, checked: !i.checked } : i) } : prev);
    await api.post(`/api/grocery/items/${item.id}/check`, { checked: !item.checked }).catch(() => load());
  }
  async function clearChecked() {
    if (!list) return;
    await api.post(`/api/grocery/lists/${list.id}/clear-checked`).catch(() => undefined);
    await load();
  }

  if (loading) return <div><div className="page-header"><h1>Grocery</h1></div><PageSpinner /></div>;

  const items = list?.items ?? [];
  const remaining = items.filter((i) => !i.checked).length;
  const byDept = departments
    .map((d) => ({ dept: d, items: items.filter((i) => i.department === d) }))
    .filter((g) => g.items.length > 0);

  return (
    <div>
      <div className="page-header">
        <div className="row-between">
          <div>
            <h1>Grocery</h1>
            <p className="subtitle">{remaining} left to grab</p>
          </div>
          {items.some((i) => i.checked) && <button className="btn btn-sm" onClick={clearChecked}>Clear checked</button>}
        </div>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Add item */}
      <div className="card">
        <div className="row wrap" style={{ gap: "var(--space-2)" }}>
          <input className="input grow" value={name} onChange={(e) => setName(e.target.value)} placeholder="Add an item" onKeyDown={(e) => { if (e.key === "Enter") void add(); }} style={{ minWidth: 160 }} />
          <select className="select" value={dept} onChange={(e) => setDept(e.target.value)} style={{ maxWidth: 160 }}>
            {departments.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <button className="btn btn-primary" onClick={add} disabled={!name.trim()}>Add</button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={<IconLeaf />} title="Your list is empty">Add items above. They group by aisle so shopping flows top-to-bottom.</EmptyState>
      ) : (
        <div className="stack mt-4">
          {byDept.map((g) => (
            <div key={g.dept}>
              <div className="nav-section-label" style={{ padding: "0 0 var(--space-2)" }}>{g.dept}</div>
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                {g.items.map((item) => (
                  <button key={item.id} className="grocery-row" onClick={() => toggle(item)} type="button">
                    <span className={`task-check${item.checked ? " done" : ""}`}>{item.checked && <IconCheck width={16} height={16} />}</span>
                    <span className="grow" style={{ textAlign: "left", textDecoration: item.checked ? "line-through" : "none", opacity: item.checked ? 0.55 : 1 }}>
                      {item.name}{item.quantity ? ` · ${item.quantity}` : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
